// External
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import clsx from 'clsx';
import { Disclosure } from '@headlessui/react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AdjustmentsHorizontalIcon,
  ChevronRightIcon,
  Squares2X2Icon,
  Bars3Icon,
  MapIcon,
} from '@heroicons/react/20/solid';

// Internal Components
import Checkbox from '@/components/Checkbox';
import Container from '@/components/Container';
import PillBadge from '@/components/PillBadge';
import EventItemsGrid from '@/components/EventItemsGrid';
import TypeaheadInput from '@/components/TypeaheadInput';
import EventsMap from '@/components/EventsMap';

// Hooks
import { useFilters } from '@/hooks/useFilters';
import { useURLSync } from '@/hooks/useURLSync';

// Utils
import {
  getStates,
  scrollTop,
  slugify,
  cleanParkData,
  formatDate,
} from '@/lib/util';
import {
  getEventsAndSortByDate,
  getStateAbbr,
  filterOutClosureEvents,
  getParkNameSelectOptions,
  statesAndParkCodes,
} from '@/lib/data';
import { getAllEvents, getParkCoordinates } from '@/lib/db-queries';

import { useRouter } from 'next/router';
import Head from 'next/head';

function FilterItem({ children }) {
  return (
    <div className="flex uppercase text-sm font-black py-4 pl-4">
      {children}
    </div>
  );
}

export default function Events({
  events,
  parkNameOptions,
  statesAndParkCodesList,
}) {
  const router = useRouter();

  // Filter state management using useFilters hook
  const {
    filters,
    setFilters,
    addFilter,
    removeFilter,
    clearFilters,
    activeFilterCount,
    activeFiltersArray,
  } = useFilters({
    park: [],
    eventType: [],
    state: [],
  });

  // URL sync for shareable links
  const { isReady: urlSyncReady } = useURLSync({
    filters,
    setFilters,
    paramMapping: {
      park: 'park',
      eventType: 'type',
      state: 'state',
    },
  });

  // Date filter state (kept separate as single values, not arrays)
  const [filterDateStart, setFilterDateStart] = useState(null);
  const [filterDateEnd, setFilterDateEnd] = useState(null);

  // Pagination state (declared early for URL sync effects)
  const [currentPage, setCurrentPage] = useState(1);

  // Mobile filter drawer state
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Ref to track if URL params have been initialized
  const isUrlInitialized = useRef(false);
  const isUpdatingUrl = useRef(false);

  // Initialize date filters and pagination from URL (once on mount)
  useEffect(() => {
    if (!router.isReady || isUrlInitialized.current) return;

    const { dateStart, dateEnd, page } = router.query;

    if (dateStart) {
      setFilterDateStart(dateStart);
    }
    if (dateEnd) {
      setFilterDateEnd(dateEnd);
    }
    if (page) {
      const pageNum = parseInt(page, 10);
      if (!isNaN(pageNum) && pageNum > 0) {
        setCurrentPage(pageNum);
      }
    }

    isUrlInitialized.current = true;
  }, [router.isReady, router.query]);

  // Sync date filters to URL when they change
  useEffect(() => {
    if (!router.isReady || !isUrlInitialized.current || isUpdatingUrl.current)
      return;

    const newQuery = { ...router.query };
    let hasChanges = false;

    // Handle dateStart
    if (filterDateStart) {
      if (newQuery.dateStart !== filterDateStart) {
        newQuery.dateStart = filterDateStart;
        hasChanges = true;
      }
    } else if (newQuery.dateStart) {
      delete newQuery.dateStart;
      hasChanges = true;
    }

    // Handle dateEnd
    if (filterDateEnd) {
      if (newQuery.dateEnd !== filterDateEnd) {
        newQuery.dateEnd = filterDateEnd;
        hasChanges = true;
      }
    } else if (newQuery.dateEnd) {
      delete newQuery.dateEnd;
      hasChanges = true;
    }

    if (hasChanges) {
      isUpdatingUrl.current = true;
      delete newQuery.page; // Reset pagination when date filters change
      router.replace(
        { pathname: router.pathname, query: newQuery },
        undefined,
        {
          shallow: true,
        }
      );
      setTimeout(() => {
        isUpdatingUrl.current = false;
      }, 100);
    }
  }, [filterDateStart, filterDateEnd, router]);

  // Sync pagination to URL when page changes
  useEffect(() => {
    if (!router.isReady || !isUrlInitialized.current || isUpdatingUrl.current)
      return;

    const currentUrlPage = router.query.page
      ? parseInt(router.query.page, 10)
      : 1;
    if (currentPage !== currentUrlPage && currentPage > 0) {
      isUpdatingUrl.current = true;
      const newQuery = { ...router.query };
      if (currentPage === 1) {
        delete newQuery.page;
      } else {
        newQuery.page = currentPage.toString();
      }
      router.replace(
        { pathname: router.pathname, query: newQuery },
        undefined,
        {
          shallow: true,
        }
      );
      setTimeout(() => {
        isUpdatingUrl.current = false;
      }, 100);
    }
  }, [currentPage, router]);

  // Search state for filter inputs
  const [parkSearchTerm, setParkSearchTerm] = useState('');
  const [typeSearchTerm, setTypeSearchTerm] = useState('');
  const [stateSearchTerm, setStateSearchTerm] = useState('');

  // UI state
  const [isListView, setIsListView] = useState(false);
  const [showMap, setShowMap] = useState(true);

  // Disclosure open state - keep panels open when they have active filters
  const [parkOpen, setParkOpen] = useState(true);
  const [eventTypeOpen, setEventTypeOpen] = useState(true);
  const [stateOpen, setStateOpen] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);

  // Keep disclosure panels open when they have active filters
  useEffect(() => {
    if (filters.park?.length > 0) setParkOpen(true);
    if (filters.eventType?.length > 0) setEventTypeOpen(true);
    if (filters.state?.length > 0) setStateOpen(true);
  }, [filters.park, filters.eventType, filters.state]);

  // Set pagination variables
  const PageSize = 12;

  // Get state options
  const states = getStates();

  // Create array with all event types for filter bar
  const types = useMemo(() => {
    const eventTypes = [];
    events &&
      events.forEach((event) => {
        if (event.types && event.types.length) {
          event.types.forEach((type) =>
            eventTypes.push({
              value: slugify(type),
              label: type,
            })
          );
        }
      });
    return [
      ...new Map(eventTypes.map((item) => [item['value'], item])).values(),
    ];
  }, [events]);

  // Filter parks to only show those with events
  const parksWithEvents = useMemo(
    () =>
      parkNameOptions
        .map((park) => {
          const hasEvents = events.some(
            (event) => (event.parkFullName || event.parkfullname) === park.label
          );
          return {
            ...park,
            disabled: !hasEvents,
          };
        })
        .filter((park) => park.label && !park.disabled),
    [parkNameOptions, events]
  );

  // Handle filter change from Checkbox component
  const handleFilterChange = useCallback(
    (e, filterType) => {
      const filterValue = e.target.dataset.value || e.target.dataset.item;
      const isChecked = e.target.checked;

      setCurrentPage(1);

      if (isChecked) {
        addFilter(filterType, filterValue);
      } else {
        removeFilter(filterType, filterValue);
      }
    },
    [addFilter, removeFilter]
  );

  // Format filter label for display in pills
  const formatFilterLabel = useCallback(
    (key, value) => {
      if (key === 'state') {
        const state = states.find((s) => s.value === value);
        return state?.label || value;
      }
      if (key === 'eventType') {
        const type = types.find((t) => t.value === value);
        return type?.label || value;
      }
      if (key === 'park') {
        const park = parkNameOptions.find((p) => p.value === value);
        return park?.label || value;
      }
      return value;
    },
    [states, types, parkNameOptions]
  );

  // Clear all filters
  const handleClearAllFilters = useCallback(() => {
    clearFilters();
    setFilterDateStart(null);
    setFilterDateEnd(null);
    setParkSearchTerm('');
    setTypeSearchTerm('');
    setStateSearchTerm('');
    setCurrentPage(1);
  }, [clearFilters]);

  // On pagination page change, update current page and scroll to the top of the events grid
  function onPageChange(page) {
    setCurrentPage(page);
    scrollTop('#events-grid');
  }

  // Create search-filtered options for the filter inputs
  const searchFilteredOptions = useMemo(() => {
    const filterBySearchTerm = (items, searchTerm) => {
      if (!searchTerm.trim()) return items;
      const term = searchTerm.toLowerCase().trim();
      return items.filter((item) => item.label.toLowerCase().includes(term));
    };

    return {
      parks: filterBySearchTerm(parksWithEvents, parkSearchTerm),
      types: filterBySearchTerm(types, typeSearchTerm),
      states: filterBySearchTerm(states, stateSearchTerm),
    };
  }, [
    parksWithEvents,
    types,
    states,
    parkSearchTerm,
    typeSearchTerm,
    stateSearchTerm,
  ]);

  // Filter events based on active filters using useMemo for better performance
  const filteredEvents = useMemo(() => {
    let result = filterOutClosureEvents(events);

    // Filter by park (using slug values stored in filters.park)
    const parkFilters = filters.park || [];
    if (parkFilters.length > 0) {
      result = result.filter((event) => {
        const parkName = event.parkFullName || event.parkfullname;
        // Find the park option to compare by label (full name)
        return parkFilters.some((parkSlug) => {
          const parkOption = parkNameOptions.find((p) => p.value === parkSlug);
          return parkOption && parkName === parkOption.label;
        });
      });
    }

    // Filter by event type (using slug values stored in filters.eventType)
    const eventTypeFilters = filters.eventType || [];
    if (eventTypeFilters.length > 0) {
      result = result.filter((event) => {
        if (!event.types || event.types.length === 0) return false;
        return eventTypeFilters.some((typeSlug) => {
          const typeOption = types.find((t) => t.value === typeSlug);
          return typeOption && event.types.includes(typeOption.label);
        });
      });
    }

    // Filter by state (using abbreviation values stored in filters.state)
    const stateFilters = filters.state || [];
    if (stateFilters.length > 0) {
      result = result.filter((event) => {
        return stateFilters.some((stateAbbr) => {
          // Get park codes for this state from pre-fetched list
          const parkCodes = statesAndParkCodesList
            .filter((item) => item.state && item.state.includes(stateAbbr))
            .map((item) => item.parkCode);
          return parkCodes.includes(event.sitecode || event.parkCode);
        });
      });
    }

    // Filter by date range
    if (filterDateStart || filterDateEnd) {
      result = result.filter((event) => {
        const effectiveStart =
          event.recurrenceDateStart ||
          event.recurrencedatestart ||
          event.dateStart ||
          event.datestart;
        const effectiveEnd =
          event.recurrenceDateEnd ||
          event.recurrencedateend ||
          event.dateEnd ||
          event.dateend ||
          effectiveStart;

        if (!effectiveStart) return false;

        const eventStartStr = effectiveStart.split('T')[0];
        const eventEndStr = (effectiveEnd || effectiveStart).split('T')[0];

        if (filterDateStart && filterDateEnd) {
          return (
            eventStartStr <= filterDateEnd && eventEndStr >= filterDateStart
          );
        } else if (filterDateStart) {
          return eventEndStr >= filterDateStart;
        } else if (filterDateEnd) {
          return eventStartStr <= filterDateEnd;
        }
        return true;
      });
    }

    return result;
  }, [
    events,
    filters,
    filterDateStart,
    filterDateEnd,
    parkNameOptions,
    types,
    statesAndParkCodesList,
  ]);

  // Paginated events for display
  const currentData = useMemo(() => {
    const firstPageIndex = (currentPage - 1) * PageSize;
    const lastPageIndex = firstPageIndex + PageSize;
    return filteredEvents.slice(firstPageIndex, lastPageIndex);
  }, [currentPage, filteredEvents]);

  // No results check
  const noResults = filteredEvents.length === 0;

  // Calculate valid filter options based on current filtered events
  // Options with 0 matching events (that aren't already selected) will be disabled
  const validFilterCounts = useMemo(() => {
    const parkCounts = {};
    const eventTypeCounts = {};
    const stateCounts = {};

    filteredEvents.forEach((event) => {
      // Count parks
      const parkName = event.parkFullName || event.parkfullname;
      if (parkName) {
        const parkOption = parkNameOptions.find((p) => p.label === parkName);
        if (parkOption) {
          parkCounts[parkOption.value] =
            (parkCounts[parkOption.value] || 0) + 1;
        }
      }

      // Count event types
      if (event.types) {
        event.types.forEach((type) => {
          const typeSlug = slugify(type);
          eventTypeCounts[typeSlug] = (eventTypeCounts[typeSlug] || 0) + 1;
        });
      }

      // Count states
      const parkCode = event.sitecode || event.parkCode;
      if (parkCode) {
        const parkStates = statesAndParkCodesList
          .filter((item) => item.parkCode === parkCode)
          .flatMap((item) => item.state || []);
        parkStates.forEach((state) => {
          stateCounts[state] = (stateCounts[state] || 0) + 1;
        });
      }
    });

    return {
      parks: parkCounts,
      eventTypes: eventTypeCounts,
      states: stateCounts,
    };
  }, [filteredEvents, parkNameOptions, statesAndParkCodesList]);

  // Helper to check if a filter option should be disabled
  const isOptionDisabled = useCallback(
    (filterType, value) => {
      // Don't disable if this option is already selected
      if (filters[filterType]?.includes(value)) return false;

      // Disable if no matching events in current filtered results
      const counts =
        filterType === 'park'
          ? validFilterCounts.parks
          : filterType === 'eventType'
            ? validFilterCounts.eventTypes
            : validFilterCounts.states;
      return (counts[value] || 0) === 0;
    },
    [filters, validFilterCounts]
  );

  // Force grid view on mobile - use requestAnimationFrame to avoid hydration mismatch
  useEffect(() => {
    function handleResize() {
      if (window.innerWidth < 768) {
        setIsListView(false);
      }
    }
    // Delay initial check until after hydration
    requestAnimationFrame(handleResize);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      <Head>
        <title>Events - Explore National Parks</title>
      </Head>
      <main>
        <Container>
          <div className="lg:grid lg:grid-cols-[300px,auto] lg:gap-8">
            <div className="hidden lg:block z-0 bg-white border border-beige-100/50 rounded">
              <div className="top-0 left-0">
                <FilterItem>
                  <AdjustmentsHorizontalIcon
                    width={18}
                    className="mr-4 -mt-1"
                  />{' '}
                  Filter & Sort
                </FilterItem>
                {/* Reset All Filters Button */}
                {activeFilterCount > 0 && (
                  <div className="border-y border-beige-100/50 px-4 py-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-brown-100">
                        Active Filters
                      </span>
                      <button
                        onClick={handleClearAllFilters}
                        className="inline-flex items-center px-3 py-1 pt-1.5 text-xxs font-bold text-brown-100 bg-gray hover:bg-black/10 border border-black/20 hover:border-black/30 rounded-full transition-all duration-200 ease-in-out"
                        title="Remove all active filters"
                      >
                        Reset All
                      </button>
                    </div>
                    <p className="text-xs text-brown-100/60 mt-1">
                      {activeFilterCount} active filters
                    </p>
                  </div>
                )}

                <Disclosure as="div" className="border-y border-beige-100/50">
                  <Disclosure.Button
                    className="flex justify-between items-center w-full tracking-wide text-xs py-4 px-4"
                    onClick={() => setParkOpen(!parkOpen)}
                  >
                    <span>National Park</span>
                    <div className="flex items-center gap-2">
                      {filters.park.length > 0 && (
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation();
                            setFilters((prev) => ({ ...prev, park: [] }));
                            setParkSearchTerm('');
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.stopPropagation();
                              setFilters((prev) => ({ ...prev, park: [] }));
                              setParkSearchTerm('');
                            }
                          }}
                          className="inline-flex items-center px-3 py-1 pt-1.5 text-xxs font-bold text-brown-100 bg-gray hover:bg-black/10 border border-black/20 hover:border-black/30 rounded-full transition-all duration-200 ease-in-out cursor-pointer"
                          title="Remove all national park filters"
                        >
                          Reset
                        </span>
                      )}
                      <ChevronRightIcon
                        width={18}
                        className={`ml-4 pointer-events-none transition-transform ${parkOpen ? 'rotate-90' : ''}`}
                      />
                    </div>
                  </Disclosure.Button>
                  {parkOpen && (
                    <div className="pb-6 pt-3 px-5">
                      {/* Typeahead input - fixed at top */}
                      <div className="mb-4 relative">
                        <TypeaheadInput
                          filterMode={true}
                          onInputChange={setParkSearchTerm}
                          value={parkSearchTerm}
                          isClearable={true}
                          placeholder={'Search parks'}
                          className={`w-full`}
                        />
                      </div>

                      {/* Scrollable checkbox list */}
                      <div className="max-h-[400px] overflow-scroll filter-scrollbar">
                        <ul className="relative w-full flex flex-col gap-2">
                          {searchFilteredOptions.parks.map((item) => (
                            <Checkbox
                              key={`park-${item.value}`}
                              label={item.label}
                              value={item.value}
                              tag="li"
                              className="flex"
                              onChange={(e) => handleFilterChange(e, 'park')}
                              filters={filters.park}
                              filterType="park"
                              disabled={isOptionDisabled('park', item.value)}
                            />
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </Disclosure>
                <Disclosure as="div" className="border-y-0">
                  <Disclosure.Button
                    className="flex justify-between items-center w-full tracking-wide text-xs py-4 px-4"
                    onClick={() => setEventTypeOpen(!eventTypeOpen)}
                  >
                    <span>Event Type</span>
                    <div className="flex items-center gap-2">
                      {filters.eventType.length > 0 && (
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation();
                            setFilters((prev) => ({
                              ...prev,
                              eventType: [],
                            }));
                            setTypeSearchTerm('');
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.stopPropagation();
                              setFilters((prev) => ({
                                ...prev,
                                eventType: [],
                              }));
                              setTypeSearchTerm('');
                            }
                          }}
                          className="inline-flex items-center px-3 py-1 pt-1.5 text-xxs font-bold text-brown-100 bg-gray hover:bg-black/10 border border-black/20 hover:border-black/30 rounded-full transition-all duration-200 ease-in-out cursor-pointer"
                          title="Remove all event type filters"
                        >
                          Reset
                        </span>
                      )}
                      <ChevronRightIcon
                        width={18}
                        className={`ml-4 pointer-events-none transition-transform ${eventTypeOpen ? 'rotate-90' : ''}`}
                      />
                    </div>
                  </Disclosure.Button>
                  {eventTypeOpen && (
                    <div className="pb-6 pt-3 px-5">
                      {/* Typeahead input - fixed at top */}
                      <div className="mb-4 relative">
                        <TypeaheadInput
                          filterMode={true}
                          onInputChange={setTypeSearchTerm}
                          value={typeSearchTerm}
                          isClearable={true}
                          placeholder={'Search event types'}
                          className={`w-full`}
                        />
                      </div>

                      {/* Scrollable checkbox list */}
                      <div className="max-h-[300px] overflow-scroll filter-scrollbar">
                        <ul className="relative w-full flex flex-col gap-2">
                          {searchFilteredOptions.types.map((item) => (
                            <Checkbox
                              key={`event-type-${item.value}`}
                              label={item.label}
                              value={item.value}
                              tag="li"
                              className="flex"
                              onChange={(e) =>
                                handleFilterChange(e, 'eventType')
                              }
                              filters={filters.eventType}
                              filterType="eventType"
                              disabled={isOptionDisabled(
                                'eventType',
                                item.value
                              )}
                            />
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </Disclosure>
                <Disclosure as="div" className="border-y border-beige-100/50">
                  <Disclosure.Button
                    className="flex justify-between items-center w-full tracking-wide text-xs py-4 px-4"
                    onClick={() => setStateOpen(!stateOpen)}
                  >
                    <span>State</span>
                    <div className="flex items-center gap-2">
                      {filters.state.length > 0 && (
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation();
                            setFilters((prev) => ({ ...prev, state: [] }));
                            setStateSearchTerm('');
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.stopPropagation();
                              setFilters((prev) => ({ ...prev, state: [] }));
                              setStateSearchTerm('');
                            }
                          }}
                          className="inline-flex items-center px-3 py-1 pt-1.5 text-xxs font-bold text-brown-100 bg-gray hover:bg-black/10 border border-black/20 hover:border-black/30 rounded-full transition-all duration-200 ease-in-out cursor-pointer"
                          title="Remove all state filters"
                        >
                          Reset
                        </span>
                      )}
                      <ChevronRightIcon
                        width={18}
                        className={`ml-4 pointer-events-none transition-transform ${stateOpen ? 'rotate-90' : ''}`}
                      />
                    </div>
                  </Disclosure.Button>
                  {stateOpen && (
                    <div className="pb-6 pt-3 px-5">
                      {/* Typeahead input - fixed at top */}
                      <div className="mb-4 relative">
                        <TypeaheadInput
                          filterMode={true}
                          onInputChange={setStateSearchTerm}
                          value={stateSearchTerm}
                          isClearable={true}
                          placeholder={'Search states'}
                          className={`w-full`}
                        />
                      </div>

                      {/* Scrollable checkbox list */}
                      <div className="max-h-[300px] overflow-scroll filter-scrollbar">
                        <ul className="relative w-full flex flex-col gap-2">
                          {searchFilteredOptions.states.map((item) => (
                            <Checkbox
                              key={`state-${item.value}`}
                              label={item.label}
                              value={item.value}
                              tag="li"
                              className="flex"
                              onChange={(e) => handleFilterChange(e, 'state')}
                              filters={filters.state}
                              filterType="state"
                              disabled={isOptionDisabled('state', item.value)}
                            />
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </Disclosure>
                {/* Date Filter */}
                <Disclosure defaultOpen={filterDateStart || filterDateEnd}>
                  {({ open }) => (
                    <div className="border-b border-beige-100/50">
                      <Disclosure.Button className="flex justify-between items-center w-full tracking-wide text-xs py-4 px-4">
                        <span>Dates</span>
                        <div className="flex items-center gap-2">
                          {(filterDateStart || filterDateEnd) && (
                            <span
                              role="button"
                              tabIndex={0}
                              onClick={(e) => {
                                e.stopPropagation();
                                setFilterDateStart(null);
                                setFilterDateEnd(null);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.stopPropagation();
                                  setFilterDateStart(null);
                                  setFilterDateEnd(null);
                                }
                              }}
                              className="inline-flex items-center px-3 py-1 pt-1.5 text-xxs font-bold text-brown-100 bg-gray hover:bg-black/10 border border-black/20 hover:border-black/30 rounded-full transition-all duration-200 ease-in-out cursor-pointer"
                              title="Reset date filters"
                            >
                              Reset
                            </span>
                          )}
                          <ChevronRightIcon
                            width={18}
                            className="ml-4 pointer-events-none ui-open:rotate-90 ui-open:transform"
                          />
                        </div>
                      </Disclosure.Button>
                      <Disclosure.Panel className="pb-6 pt-3 px-5">
                        <div className="flex flex-col gap-2">
                          <div>
                            <label className="block text-xxs font-bold text-brown-100 mb-2">
                              From
                            </label>
                            <input
                              type="date"
                              value={filterDateStart || ''}
                              onChange={(e) =>
                                setFilterDateStart(e.target.value || null)
                              }
                              className="w-full h-[40px] px-3 border border-beige-100 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-brown-400"
                            />
                          </div>
                          <div>
                            <label className="block text-xxs font-bold text-brown-100 mb-2">
                              To
                            </label>
                            <input
                              type="date"
                              value={filterDateEnd || ''}
                              onChange={(e) =>
                                setFilterDateEnd(e.target.value || null)
                              }
                              min={filterDateStart || undefined}
                              className="w-full h-[40px] px-3 border border-beige-100 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-brown-400"
                            />
                          </div>
                        </div>
                      </Disclosure.Panel>
                    </div>
                  )}
                </Disclosure>
              </div>
            </div>

            <div className="mt-5 lg:mt-0">
              <h1 className="sr-only">Events</h1>

              {/* Mobile Filter Button */}
              <div className="lg:hidden mb-4">
                <button
                  onClick={() => setShowMobileFilters(true)}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm text-brown-100 bg-beige-50 rounded-lg hover:bg-beige-100 transition-colors"
                >
                  <AdjustmentsHorizontalIcon className="h-4 w-4" />
                  Filters
                  {activeFilterCount > 0 && (
                    <span className="bg-green-100 text-white text-xs px-1.5 rounded-full">
                      {activeFilterCount}
                    </span>
                  )}
                </button>
              </div>

              {/* Events Map */}
              {showMap && (
                <div className="mb-4">
                  <EventsMap
                    events={filteredEvents}
                    selectedStates={filters.state || []}
                  />
                </div>
              )}

              {/* Count and Grid/List Toggle - BELOW MAP */}
              <div className="flex justify-between items-center my-4">
                <p>
                  <strong>{filteredEvents.length}</strong>{' '}
                  {filteredEvents.length === 1 ? 'event' : 'events'}
                </p>
                <div className="flex items-center gap-2">
                  {/* Map Toggle */}
                  <button
                    onClick={() => setShowMap(!showMap)}
                    className={clsx(
                      'flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg transition-colors',
                      showMap
                        ? 'bg-green-100 text-white'
                        : 'text-brown-100 bg-beige-50 hover:bg-beige-100'
                    )}
                  >
                    <MapIcon className="h-4 w-4" />
                    Map
                  </button>
                  {/* Grid/List Toggle - hidden on mobile */}
                  <div className="hidden md:flex items-center gap-1">
                    <button
                      onClick={() => setIsListView(true)}
                      className={clsx(
                        'p-1.5 rounded-lg transition-colors',
                        isListView
                          ? 'bg-beige-100 text-brown-100'
                          : 'text-brown-300 hover:bg-beige-50'
                      )}
                      title="List view"
                    >
                      <Bars3Icon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => setIsListView(false)}
                      className={clsx(
                        'p-1.5 rounded-lg transition-colors',
                        !isListView
                          ? 'bg-beige-100 text-brown-100'
                          : 'text-brown-300 hover:bg-beige-50'
                      )}
                      title="Grid view"
                    >
                      <Squares2X2Icon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
              {activeFilterCount > 0 || filterDateStart || filterDateEnd ? (
                <nav className="mt-4">
                  <ul className="flex flex-wrap items-center gap-2">
                    {activeFiltersArray.map(({ key, value }, index) => (
                      <PillBadge
                        key={`${key}-${index}-${value}`}
                        item={{
                          label: formatFilterLabel(key, value),
                          value: value,
                        }}
                        onClick={() => removeFilter(key, value)}
                      />
                    ))}
                    {filterDateStart && (
                      <PillBadge
                        key="date-start"
                        item={{
                          label: `From: ${formatDate(filterDateStart)}`,
                          value: 'date-start',
                        }}
                        onClick={() => setFilterDateStart(null)}
                      />
                    )}
                    {filterDateEnd && (
                      <PillBadge
                        key="date-end"
                        item={{
                          label: `To: ${formatDate(filterDateEnd)}`,
                          value: 'date-end',
                        }}
                        onClick={() => setFilterDateEnd(null)}
                      />
                    )}
                  </ul>
                </nav>
              ) : null}
              {noResults && (
                <p className="mt-4 text-sm">
                  No events for the selected filters. Try adjusting your
                  filters.
                </p>
              )}

              <EventItemsGrid
                events={currentData}
                gridClassNames={`grid gap-6 my-5 ${isListView ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'}`}
                isListView={isListView}
                currentPage={currentPage}
                totalCount={filteredEvents.length}
                pageSize={PageSize}
                onPageChange={onPageChange}
                displayLoadMore
                showPagination
              />
            </div>
          </div>
        </Container>

        {/* Mobile Filters Modal */}
        <AnimatePresence>
          {showMobileFilters && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-[1000] lg:hidden"
              onClick={(e) => {
                if (e.target === e.currentTarget) {
                  setShowMobileFilters(false);
                }
              }}
            >
              <motion.div
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ type: 'spring', damping: 25 }}
                className="absolute right-0 top-0 bottom-0 w-80 max-w-full bg-white overflow-y-auto"
              >
                <div className="p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-serif font-bold text-lg">Filters</h2>
                    <button
                      onClick={() => setShowMobileFilters(false)}
                      className="text-brown-300 hover:text-brown-100"
                    >
                      Close
                    </button>
                  </div>

                  {/* National Park Filter */}
                  <Disclosure as="div" className="border-y border-beige-100/50">
                    <Disclosure.Button
                      className="flex justify-between items-center w-full tracking-wide text-xs py-4 px-2"
                      onClick={() => setParkOpen(!parkOpen)}
                    >
                      <span>National Park</span>
                      <div className="flex items-center gap-2">
                        {filters.park.length > 0 && (
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => {
                              e.stopPropagation();
                              setFilters((prev) => ({ ...prev, park: [] }));
                              setParkSearchTerm('');
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.stopPropagation();
                                setFilters((prev) => ({ ...prev, park: [] }));
                                setParkSearchTerm('');
                              }
                            }}
                            className="inline-flex items-center px-2 py-0.5 text-xxs font-bold text-brown-100 bg-gray hover:bg-black/10 border border-black/20 rounded-full cursor-pointer"
                          >
                            Reset
                          </span>
                        )}
                        <ChevronRightIcon
                          width={18}
                          className={`pointer-events-none transition-transform ${parkOpen ? 'rotate-90' : ''}`}
                        />
                      </div>
                    </Disclosure.Button>
                    {parkOpen && (
                      <div className="pb-4 px-2">
                        <div className="mb-3">
                          <TypeaheadInput
                            filterMode={true}
                            onInputChange={setParkSearchTerm}
                            value={parkSearchTerm}
                            isClearable={true}
                            placeholder="Search parks"
                            className="w-full"
                          />
                        </div>
                        <div className="max-h-[200px] overflow-scroll filter-scrollbar">
                          <ul className="flex flex-col gap-2">
                            {searchFilteredOptions.parks.map((item) => (
                              <Checkbox
                                key={`mobile-park-${item.value}`}
                                label={item.label}
                                value={item.value}
                                tag="li"
                                className="flex"
                                onChange={(e) => handleFilterChange(e, 'park')}
                                filters={filters.park}
                                filterType="park"
                                disabled={isOptionDisabled('park', item.value)}
                              />
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                  </Disclosure>

                  {/* Event Type Filter */}
                  <Disclosure as="div" className="border-b border-beige-100/50">
                    <Disclosure.Button
                      className="flex justify-between items-center w-full tracking-wide text-xs py-4 px-2"
                      onClick={() => setEventTypeOpen(!eventTypeOpen)}
                    >
                      <span>Event Type</span>
                      <div className="flex items-center gap-2">
                        {filters.eventType.length > 0 && (
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => {
                              e.stopPropagation();
                              setFilters((prev) => ({
                                ...prev,
                                eventType: [],
                              }));
                              setTypeSearchTerm('');
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.stopPropagation();
                                setFilters((prev) => ({
                                  ...prev,
                                  eventType: [],
                                }));
                                setTypeSearchTerm('');
                              }
                            }}
                            className="inline-flex items-center px-2 py-0.5 text-xxs font-bold text-brown-100 bg-gray hover:bg-black/10 border border-black/20 rounded-full cursor-pointer"
                          >
                            Reset
                          </span>
                        )}
                        <ChevronRightIcon
                          width={18}
                          className={`pointer-events-none transition-transform ${eventTypeOpen ? 'rotate-90' : ''}`}
                        />
                      </div>
                    </Disclosure.Button>
                    {eventTypeOpen && (
                      <div className="pb-4 px-2">
                        <div className="mb-3">
                          <TypeaheadInput
                            filterMode={true}
                            onInputChange={setTypeSearchTerm}
                            value={typeSearchTerm}
                            isClearable={true}
                            placeholder="Search event types"
                            className="w-full"
                          />
                        </div>
                        <div className="max-h-[200px] overflow-scroll filter-scrollbar">
                          <ul className="flex flex-col gap-2">
                            {searchFilteredOptions.types.map((item) => (
                              <Checkbox
                                key={`mobile-type-${item.value}`}
                                label={item.label}
                                value={item.value}
                                tag="li"
                                className="flex"
                                onChange={(e) =>
                                  handleFilterChange(e, 'eventType')
                                }
                                filters={filters.eventType}
                                filterType="eventType"
                                disabled={isOptionDisabled(
                                  'eventType',
                                  item.value
                                )}
                              />
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                  </Disclosure>

                  {/* State Filter */}
                  <Disclosure as="div" className="border-b border-beige-100/50">
                    <Disclosure.Button
                      className="flex justify-between items-center w-full tracking-wide text-xs py-4 px-2"
                      onClick={() => setStateOpen(!stateOpen)}
                    >
                      <span>State</span>
                      <div className="flex items-center gap-2">
                        {filters.state.length > 0 && (
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => {
                              e.stopPropagation();
                              setFilters((prev) => ({ ...prev, state: [] }));
                              setStateSearchTerm('');
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.stopPropagation();
                                setFilters((prev) => ({ ...prev, state: [] }));
                                setStateSearchTerm('');
                              }
                            }}
                            className="inline-flex items-center px-2 py-0.5 text-xxs font-bold text-brown-100 bg-gray hover:bg-black/10 border border-black/20 rounded-full cursor-pointer"
                          >
                            Reset
                          </span>
                        )}
                        <ChevronRightIcon
                          width={18}
                          className={`pointer-events-none transition-transform ${stateOpen ? 'rotate-90' : ''}`}
                        />
                      </div>
                    </Disclosure.Button>
                    {stateOpen && (
                      <div className="pb-4 px-2">
                        <div className="mb-3">
                          <TypeaheadInput
                            filterMode={true}
                            onInputChange={setStateSearchTerm}
                            value={stateSearchTerm}
                            isClearable={true}
                            placeholder="Search states"
                            className="w-full"
                          />
                        </div>
                        <div className="max-h-[200px] overflow-scroll filter-scrollbar">
                          <ul className="flex flex-col gap-2">
                            {searchFilteredOptions.states.map((item) => (
                              <Checkbox
                                key={`mobile-state-${item.value}`}
                                label={item.label}
                                value={item.value}
                                tag="li"
                                className="flex"
                                onChange={(e) => handleFilterChange(e, 'state')}
                                filters={filters.state}
                                filterType="state"
                                disabled={isOptionDisabled('state', item.value)}
                              />
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                  </Disclosure>

                  {/* Date Range Filter */}
                  <Disclosure as="div" className="border-b border-beige-100/50">
                    <Disclosure.Button
                      className="flex justify-between items-center w-full tracking-wide text-xs py-4 px-2"
                      onClick={() => setDateOpen(!dateOpen)}
                    >
                      <span>Date Range</span>
                      <div className="flex items-center gap-2">
                        {(filterDateStart || filterDateEnd) && (
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => {
                              e.stopPropagation();
                              setFilterDateStart(null);
                              setFilterDateEnd(null);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.stopPropagation();
                                setFilterDateStart(null);
                                setFilterDateEnd(null);
                              }
                            }}
                            className="inline-flex items-center px-2 py-0.5 text-xxs font-bold text-brown-100 bg-gray hover:bg-black/10 border border-black/20 rounded-full cursor-pointer"
                          >
                            Reset
                          </span>
                        )}
                        <ChevronRightIcon
                          width={18}
                          className={`pointer-events-none transition-transform ${dateOpen ? 'rotate-90' : ''}`}
                        />
                      </div>
                    </Disclosure.Button>
                    {dateOpen && (
                      <div className="pb-4 px-2">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xxs font-bold text-brown-100 mb-2">
                              From
                            </label>
                            <input
                              type="date"
                              value={filterDateStart || ''}
                              onChange={(e) =>
                                setFilterDateStart(e.target.value || null)
                              }
                              className="w-full h-[40px] px-3 border border-beige-100 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-brown-400"
                            />
                          </div>
                          <div>
                            <label className="block text-xxs font-bold text-brown-100 mb-2">
                              To
                            </label>
                            <input
                              type="date"
                              value={filterDateEnd || ''}
                              onChange={(e) =>
                                setFilterDateEnd(e.target.value || null)
                              }
                              min={filterDateStart || undefined}
                              className="w-full h-[40px] px-3 border border-beige-100 text-sm rounded-sm focus:outline-none focus:ring-2 focus:ring-brown-400"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </Disclosure>

                  {/* Apply Button */}
                  <div className="mt-6">
                    <button
                      onClick={() => setShowMobileFilters(false)}
                      className="w-full py-3 bg-green-100 text-white font-semibold rounded-xl hover:bg-green-200 transition-colors"
                    >
                      Show {filteredEvents.length} events
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </>
  );
}

export async function getStaticProps() {
  try {
    // Fetch from Supabase database
    const [eventsData, parkNameOptions, statesAndParkCodesList, parksData] =
      await Promise.all([
        getAllEvents(),
        getParkNameSelectOptions(),
        statesAndParkCodes(),
        getParkCoordinates(),
      ]);

    // Create a map of parkCode -> coordinates for quick lookup
    const parkCoordinates = new Map();
    parksData.forEach((park) => {
      if (park.parkCode && park.latitude && park.longitude) {
        parkCoordinates.set(park.parkCode, {
          latitude: park.latitude,
          longitude: park.longitude,
        });
      }
    });

    // Filter and sort events
    let events = filterOutClosureEvents(eventsData);
    events = getEventsAndSortByDate(events);

    // Clean the events data and add park coordinates if event doesn't have its own
    const cleanedEvents = events.map((event) => {
      const cleaned = cleanParkData(event);
      // If event doesn't have coordinates, use park coordinates
      if ((!cleaned.latitude || !cleaned.longitude) && cleaned.parkCode) {
        const parkCoords = parkCoordinates.get(cleaned.parkCode);
        if (parkCoords) {
          cleaned.latitude = parkCoords.latitude;
          cleaned.longitude = parkCoords.longitude;
        }
      }
      return cleaned;
    });

    return {
      props: {
        events: cleanedEvents,
        parkNameOptions: parkNameOptions || [],
        statesAndParkCodesList: statesAndParkCodesList || [],
      },
      revalidate: 86400, // Regenerate every 24 hours (data syncs weekly)
    };
  } catch (error) {
    console.error('Error loading events data:', error);
    return {
      props: {
        events: [],
        parkNameOptions: [],
        statesAndParkCodesList: [],
      },
      revalidate: 60, // Retry sooner on error
    };
  }
}
